import React from 'react'

import CachedIcon from '@mui/icons-material/Cached'
import { Box, IconButton, Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import { format } from 'date-fns'
import { de, enUS, es, fr, it, uk } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

type RefreshStatsType = {
  onClick: () => void
  lastAccountUpdate?: string
  isRunningAutoUpload: boolean
  isMobile?: boolean
  isLoading?: boolean
  isAutoSync?: boolean
}

export const RefreshStats: React.FC<RefreshStatsType> = ({
  onClick,
  lastAccountUpdate,
  isRunningAutoUpload,
  isMobile,
  isLoading,
  isAutoSync,
}) => {
  const { t, i18n } = useTranslation()

  const { spacing, palette } = useTheme()
  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
  const dateLocale = { en: enUS, uk, es, fr, it, de }[language] || enUS

  if (!isAutoSync && !isLoading) {
    return null
  }

  if (isMobile) {
    return (
      <RefreshStatsMobile
        onClick={onClick}
        isRunningAutoUpload={isRunningAutoUpload}
        isLoading={isLoading}
      />
    )
  }

  return (
    <Box display="flex" alignItems="center" gap={spacing(1)}>
      {/* Skeleton or last update */}
      <Typography color={palette.grey[700]} fontWeight={500} textAlign={'right'} component={'div'}>
        <>
          {isLoading ? (
            <Box display="flex" justifyContent="flex-end">
              <Skeleton width={90} />
            </Box>
          ) : (
            t('lastUpdate', 'Last update')
          )}
          {isLoading ? null : <br />}
          {isLoading ? (
            <Box display="flex" justifyContent="flex-end">
              <Skeleton width={140} />
            </Box>
          ) : lastAccountUpdate ? (
            format(new Date(lastAccountUpdate), 'HH:mm dd MMM yyyy', { locale: dateLocale })
          ) : (
            '-'
          )}
        </>
      </Typography>

      {/* Skeleton for refresh button */}
      {isLoading ? (
        <Skeleton variant="circular" width={30} height={30} />
      ) : (
        <Tooltip title={t('refreshData', 'Refresh data')} placement="top">
          <IconButton color="primary" onClick={onClick} disabled={isRunningAutoUpload}>
            <CachedIcon className={isRunningAutoUpload ? 'spin' : ''} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

export const RefreshStatsMobile: React.FC<Omit<RefreshStatsType, 'lastAccountUpdate'>> = ({
  onClick,
  isRunningAutoUpload,
  isLoading,
}) => {
  const { t } = useTranslation()

  if (isLoading) {
    return <Skeleton variant="circular" width={30} height={30} />
  }

  return (
    <Tooltip title={t('refreshData', 'Refresh data')} placement="bottom">
      <IconButton color="primary" onClick={onClick} disabled={isRunningAutoUpload}>
        <CachedIcon className={isRunningAutoUpload ? 'spin' : ''} />
      </IconButton>
    </Tooltip>
  )
}
